import { createContext, useCallback, useContext, useEffect, useState, ReactNode } from "react";
import type { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";

export type AppRole =
  | "super_admin"
  | "marketing_admin"
  | "hr_manager"
  | "hr_support"
  | "customer_service_manager"
  | "sales_manager";

export const ROLE_LABEL: Record<AppRole, string> = {
  super_admin: "Super Admin",
  marketing_admin: "Marketing Admin",
  hr_manager: "HR Manager",
  hr_support: "HR Support",
  customer_service_manager: "Customer Service",
  sales_manager: "Sales Manager",
};

interface Profile {
  id: string;
  email: string;
  full_name: string | null;
  avatar_url: string | null;
  phone?: string | null;
  service_address?: string | null;
  city?: string | null;
  customer_type?: string | null;
  preferred_language?: string | null;
  communication_preference?: string | null;
  is_onboarded?: boolean | null;
  must_change_password?: boolean | null;
}

interface AuthCtx {
  user: User | null;
  session: Session | null;
  profile: Profile | null;
  roles: AppRole[];
  primaryRole: AppRole | null;
  loading: boolean;
  rolesReady: boolean;
  isAdmin: boolean;
  hasRole: (r: AppRole | AppRole[]) => boolean;
  refresh: () => Promise<void>;
  signOut: () => Promise<void>;
}

const Ctx = createContext<AuthCtx | undefined>(undefined);

const ROLE_PRIORITY: AppRole[] = [
  "super_admin",
  "marketing_admin",
  "hr_manager",
  "hr_support",
  "customer_service_manager",
  "sales_manager",
];

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [roles, setRoles] = useState<AppRole[]>([]);
  const [loading, setLoading] = useState(true);
  const [rolesReady, setRolesReady] = useState(false);

  const loadProfileAndRoles = useCallback(async (uid: string | undefined, userObj?: User | null) => {
    if (!uid) {
      setProfile(null);
      setRoles([]);
      setRolesReady(true);
      return;
    }
    setRolesReady(false);
    let [{ data: prof }, { data: rs }] = await Promise.all([
      supabase.from("profiles").select("*").eq("id", uid).maybeSingle(),
      supabase.from("user_roles").select("role").eq("user_id", uid),
    ]);
    // Belt-and-suspenders: if the handle_new_user trigger missed this user
    // (e.g. legacy Google OAuth signup), backfill a minimal profile so admins
    // can see the account immediately.
    if (!prof && userObj) {
      const fullName =
        (userObj.user_metadata as any)?.full_name ||
        (userObj.user_metadata as any)?.name ||
        userObj.email ||
        null;
      const { data: created } = await supabase
        .from("profiles")
        .insert({ id: uid, email: userObj.email!, full_name: fullName, is_onboarded: false } as any)
        .select("*")
        .maybeSingle();
      prof = created ?? prof;
    }
    setProfile((prof as Profile) ?? null);
    setRoles(((rs ?? []) as { role: AppRole }[]).map((r) => r.role));
    setRolesReady(true);
  }, []);

  useEffect(() => {
    let mounted = true;

    const REQUIRED = ["full_name", "phone", "service_address", "city", "customer_type", "preferred_language", "communication_preference"] as const;
    const isComplete = (p: any) => !!p && REQUIRED.every((k) => typeof p[k] === "string" && p[k].trim().length > 0);

    const routeAfterSignIn = (prof: Profile | null, rs: AppRole[]) => {
      if (typeof window === "undefined") return;
      const path = window.location.pathname;
      const search = window.location.search;
      const isAuthPath = ["/login", "/signup", "/auth", "/auth/callback"].some((p) => path === p || path.startsWith(p + "/"));
      const isOnboarding = path === "/onboarding";
      if (!isAuthPath && !isOnboarding) return;

      const params = new URLSearchParams(search);
      const redirectParam = params.get("redirect");

      const isStaffEmail = (prof?.email || "").toLowerCase().endsWith("@calwaste.com");

      let target: string;
      if (rs.length > 0) {
        target = "/cws-admin";
      } else if (isStaffEmail) {
        // Staff with no role yet still belong in the admin area, not the customer dashboard.
        target = "/cws-admin";
      } else if (!isComplete(prof) || prof?.is_onboarded === false) {
        target = "/onboarding";
      } else {
        target = redirectParam && redirectParam.startsWith("/") ? redirectParam : "/dashboard";
      }
      if (path === target) return;
      if (isOnboarding && target === "/onboarding") return;
      window.history.replaceState({}, "", target);
      // dispatch popstate so react-router picks it up
      window.dispatchEvent(new PopStateEvent("popstate"));
    };

    const { data: sub } = supabase.auth.onAuthStateChange((event, sess) => {
      setSession(sess);
      setUser(sess?.user ?? null);
      // Defer to avoid deadlocks inside the auth callback
      setTimeout(async () => {
        if (sess?.user) {
          const uid = sess.user.id;
          setRolesReady(false);
          let [{ data: prof }, { data: rs }] = await Promise.all([
            supabase.from("profiles").select("*").eq("id", uid).maybeSingle(),
            supabase.from("user_roles").select("role").eq("user_id", uid),
          ]);
          if (!prof) {
            const fullName = (sess.user.user_metadata as any)?.full_name || (sess.user.user_metadata as any)?.name || sess.user.email || null;
            const { data: created } = await supabase
              .from("profiles")
              .insert({ id: uid, email: sess.user.email!, full_name: fullName, is_onboarded: false } as any)
              .select("*")
              .maybeSingle();
            prof = created ?? prof;
          }
          const roleList = ((rs ?? []) as { role: AppRole }[]).map((r) => r.role);
          setProfile((prof as Profile) ?? null);
          setRoles(roleList);
          setRolesReady(true);
          if (mounted) setLoading(false);

          if (event === "SIGNED_IN") {
            routeAfterSignIn((prof as Profile) ?? null, roleList);

            const isAdminUser = roleList.length > 0;
            supabase.from("portal_activity_events").insert({
              user_id: sess.user.id,
              user_email: sess.user.email,
              user_type: isAdminUser ? "admin" : "customer",
              event_type: isAdminUser ? "admin_login" : "customer_login",
              module: isAdminUser ? "admin" : "customer_portal",
              page_path: typeof window !== "undefined" ? window.location.pathname : null,
            } as any).then(() => {});
            supabase.from("activity_logs").insert({
              actor_user_id: sess.user.id,
              actor_email: sess.user.email,
              action: "user_login",
              module: "auth",
              user_agent: typeof navigator !== "undefined" ? navigator.userAgent : null,
            } as any).then(() => {});
          }
        } else {
          await loadProfileAndRoles(undefined, null);
          if (mounted) setLoading(false);
        }
      }, 0);
    });

    supabase.auth.getSession().then(async ({ data: { session: s } }) => {
      setSession(s);
      setUser(s?.user ?? null);
      await loadProfileAndRoles(s?.user?.id, s?.user ?? null);
      if (mounted) setLoading(false);
    });

    return () => {
      mounted = false;
      sub.subscription.unsubscribe();
    };
  }, [loadProfileAndRoles]);

  // HR Support has the exact same access as HR Manager, so any guard that
  // checks for hr_manager is satisfied by hr_support too.
  const effectiveRoles: AppRole[] = roles.includes("hr_support") && !roles.includes("hr_manager")
    ? [...roles, "hr_manager"]
    : roles;

  const hasRole = (r: AppRole | AppRole[]) => {
    if (roles.includes("super_admin")) return true;
    const list = Array.isArray(r) ? r : [r];
    return list.some((x) => effectiveRoles.includes(x));
  };

  const primaryRole = ROLE_PRIORITY.find((r) => roles.includes(r)) ?? null;

  return (
    <Ctx.Provider
      value={{
        user,
        session,
        profile,
        roles,
        primaryRole,
        loading,
        rolesReady,
        isAdmin: roles.length > 0,
        hasRole,
        refresh: () => loadProfileAndRoles(user?.id, user),
        signOut: async () => {
          if (user) {
            await supabase.from("activity_logs").insert({
              actor_user_id: user.id,
              actor_email: user.email,
              action: "user_logout",
              module: "auth",
            } as any);
          }
          await supabase.auth.signOut();
          setProfile(null);
          setRoles([]);
        },
      }}
    >
      {children}
    </Ctx.Provider>
  );
};

export const useAuth = () => {
  const c = useContext(Ctx);
  if (!c) throw new Error("useAuth must be inside AuthProvider");
  return c;
};
